import fs from "node:fs";
import { createRequire } from "node:module";
import path from "node:path";
import { pathToFileURL } from "node:url";
import type { Jiti } from "jiti";
import { createJiti } from "jiti";
import { AbstractGadget } from "llmist";
import { getBuiltinGadget, getBuiltinGadgetNames, isBuiltinGadgetName } from "./builtins/index.js";
import { isExternalPackageSpecifier, loadExternalGadgets } from "./external-gadgets.js";
import { expandTildePath } from "./paths.js";

/**
 * Function type for importing modules dynamically.
 */
export type GadgetImportFunction = (specifier: string) => Promise<unknown>;

const PATH_PREFIXES = [".", "/", "~"];
const BUILTIN_PREFIX = "builtin:";

/**
 * TypeScript file extensions that require runtime transpilation.
 */
const TYPESCRIPT_EXTENSIONS = [".ts", ".tsx", ".mts", ".cts"];

/**
 * Checks if a file path requires TypeScript transpilation.
 *
 * @param specifier - The file URL or path to check
 * @returns True if the file is a TypeScript file
 */
export function isTypeScriptFile(specifier: string): boolean {
  // Handle file:// URLs
  const pathToCheck = specifier.startsWith("file://") ? new URL(specifier).pathname : specifier;
  return TYPESCRIPT_EXTENSIONS.some((ext) => pathToCheck.endsWith(ext));
}

/**
 * Resolves the path to an installed package from the CLI's node_modules.
 * Uses Node's createRequire to leverage standard module resolution.
 *
 * This allows TypeScript gadgets in ~/.llmist/gadgets/ to import from
 * 'llmist' without needing their own node_modules directory.
 */
function resolvePackagePath(packageName: string): string {
  const require = createRequire(import.meta.url);
  return require.resolve(packageName);
}

/**
 * Lazy-initialized JITI instance (singleton per process).
 * Uses filesystem caching for performance.
 */
let jitiInstance: Jiti | null = null;

function getJiti(): Jiti {
  if (!jitiInstance) {
    jitiInstance = createJiti(import.meta.url, {
      // Enable filesystem cache for performance (default location)
      fsCache: true,
      // Enable module cache to integrate with Node.js cache
      moduleCache: true,
      // Enable interop for CJS/ESM compatibility
      interopDefault: true,
      // Map 'llmist' and 'zod' to CLI's installed versions
      // Allows TypeScript gadgets in ~/.llmist/gadgets/ to import
      // without needing their own node_modules
      alias: {
        llmist: resolvePackagePath("llmist"),
        zod: resolvePackagePath("zod"),
      },
    });
  }
  return jitiInstance;
}

/**
 * Creates an importer function that handles both JavaScript and TypeScript files.
 * JavaScript files use native import(), TypeScript files use JITI for transpilation.
 *
 * @returns Importer function for loadGadgets
 */
export function createTypeScriptImporter(): GadgetImportFunction {
  return async (specifier: string): Promise<unknown> => {
    if (isTypeScriptFile(specifier)) {
      const jiti = getJiti();
      return await jiti.import(specifier);
    }
    // JavaScript files use native import
    return import(specifier);
  };
}

/**
 * Duck-type check if a value looks like a Gadget instance.
 * This avoids instanceof issues when gadgets are loaded from external files
 * that import from the 'llmist' npm package (different class instance).
 */
function isGadgetLike(value: unknown): value is AbstractGadget {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.execute === "function" &&
    typeof obj.description === "string" &&
    ("parameterSchema" in obj || "schema" in obj)
  );
}

/**
 * Type guard to check if a value is a Gadget constructor.
 * Checks if the prototype has an execute method (gadget classes created via Gadget()
 * factory put description/schema on instances, not prototype, so we just check for execute).
 *
 * @param value - Value to check
 * @returns True if value is a Gadget constructor
 */
function isGadgetConstructor(value: unknown): value is new () => AbstractGadget {
  if (typeof value !== "function") {
    return false;
  }

  const prototype = value.prototype as unknown;
  if (!prototype) {
    return false;
  }

  // Check for AbstractGadget inheritance (works for same package)
  if (prototype instanceof AbstractGadget) {
    return true;
  }

  // Duck typing: check if prototype has execute method
  // Gadget classes from external packages may not pass instanceof but have execute
  const proto = prototype as Record<string, unknown>;
  if (typeof proto.execute === "function") {
    return true;
  }

  // Also check if prototype looks like a gadget (for edge cases)
  return isGadgetLike(prototype);
}

/**
 * Parsed local file specifier with optional gadget name.
 */
interface LocalSpecifier {
  path: string;
  gadgetName?: string;
}

/**
 * Parse a local file specifier that may include a :GadgetName suffix.
 * The gadget name must be PascalCase (start with uppercase letter).
 * Uses colon separator to avoid ambiguity with filesystem paths.
 *
 * @param specifier - Local file path, possibly with :GadgetName suffix
 * @returns Parsed path and optional gadget name
 *
 * @example
 * parseLocalSpecifier("~/gadgets/index.ts:BrowseWeb")
 * // => { path: "~/gadgets/index.ts", gadgetName: "BrowseWeb" }
 *
 * parseLocalSpecifier("~/gadgets/index.ts")
 * // => { path: "~/gadgets/index.ts" }
 */
function parseLocalSpecifier(specifier: string): LocalSpecifier {
  // Match trailing :PascalCaseName (colon + uppercase letter followed by alphanumeric)
  // Use colon separator to avoid ambiguity with filesystem paths
  const match = specifier.match(/^(.+):([A-Z][a-zA-Z0-9]*)$/);
  if (match) {
    const [, basePath, gadgetName] = match;
    return { path: basePath, gadgetName };
  }
  return { path: specifier };
}

/**
 * Determines if a specifier is a file path vs npm module name.
 * File paths start with ., /, ~ or contain path separators.
 *
 * @param specifier - Module specifier to check
 * @returns True if specifier represents a file path
 */
function isFileLikeSpecifier(specifier: string): boolean {
  return (
    PATH_PREFIXES.some((prefix) => specifier.startsWith(prefix)) || specifier.includes(path.sep)
  );
}

/**
 * Attempts to resolve a specifier as a built-in gadget.
 * Handles both explicit "builtin:" prefix and bare names that match built-in gadgets.
 *
 * @param specifier - The gadget specifier to check
 * @returns The built-in gadget if found, null otherwise
 * @throws Error if "builtin:" prefix is used but gadget doesn't exist
 */
export function tryResolveBuiltin(specifier: string): AbstractGadget | null {
  // Handle explicit builtin: prefix
  if (specifier.startsWith(BUILTIN_PREFIX)) {
    const name = specifier.slice(BUILTIN_PREFIX.length);
    const gadget = getBuiltinGadget(name);
    if (!gadget) {
      throw new Error(
        `Unknown builtin gadget: ${name}. Available builtins: ${getBuiltinGadgetNames().join(", ")}`,
      );
    }
    return gadget;
  }

  // For non-file-path specifiers, check builtins first
  if (!isFileLikeSpecifier(specifier) && isBuiltinGadgetName(specifier)) {
    return getBuiltinGadget(specifier)!;
  }

  return null;
}

/**
 * Resolves a gadget specifier to either a file URL or npm module name.
 * File paths are resolved relative to cwd and converted to file:// URLs.
 *
 * @param specifier - Original gadget specifier (file path or module name)
 * @param cwd - Current working directory for resolving relative paths
 * @returns Resolved specifier (file:// URL for files, module name for packages)
 * @throws Error if file path doesn't exist
 */
export function resolveGadgetSpecifier(specifier: string, cwd: string): string {
  if (!isFileLikeSpecifier(specifier)) {
    return specifier;
  }

  const expanded = expandTildePath(specifier);
  const resolvedPath = path.resolve(cwd, expanded);
  if (!fs.existsSync(resolvedPath)) {
    throw new Error(`Gadget module not found at ${resolvedPath}`);
  }
  return pathToFileURL(resolvedPath).href;
}

/**
 * Recursively extracts all Gadget instances and classes from a module's exports.
 * Searches default export, named exports, nested objects, and arrays.
 * Automatically instantiates Gadget classes.
 *
 * @param moduleExports - Module exports object to search
 * @returns Array of Gadget instances found in exports
 */
export function extractGadgetsFromModule(moduleExports: unknown): AbstractGadget[] {
  const results: AbstractGadget[] = [];
  const visited = new Set<unknown>();

  const visit = (value: unknown) => {
    if (value === undefined || value === null) {
      return;
    }

    if (visited.has(value)) {
      return;
    }
    visited.add(value);

    // Use duck typing to handle gadgets from external packages
    if (value instanceof AbstractGadget || isGadgetLike(value)) {
      results.push(value as AbstractGadget);
      return;
    }

    if (isGadgetConstructor(value)) {
      results.push(new value());
      return;
    }

    if (Array.isArray(value)) {
      for (const entry of value) {
        visit(entry);
      }
      return;
    }

    if (typeof value === "object") {
      for (const entry of Object.values(value as Record<string, unknown>)) {
        visit(entry);
      }
    }
  };

  visit(moduleExports);
  return results;
}

/**
 * Loads gadgets from one or more specifiers.
 * Supports built-in gadgets, file paths, npm module names, and external packages.
 *
 * Resolution order:
 * 1. "builtin:Name" - explicit built-in lookup (error if not found)
 * 2. Bare "Name" without path chars - check built-in registry first
 * 3. External packages (npm/git) - auto-install and load from cache
 *    - "dhalsim" - npm package, all gadgets
 *    - "dhalsim@2.0.0" - npm package with version
 *    - "dhalsim:minimal" - npm package with preset
 *    - "dhalsim/Navigate" - npm package with specific gadget
 *    - "git+https://..." - git URL
 *    - "git+https://...#ref/GadgetName" - git URL with specific gadget
 * 4. File paths (starting with ., /, ~) - resolve and import
 *    - "~/path/to/gadgets.ts" - all gadgets from module
 *    - "~/path/to/gadgets.ts:BrowseWeb" - specific gadget by name (colon separator)
 * 5. npm module names - dynamic import from node_modules
 *
 * @param specifiers - Array of gadget specifiers
 * @param cwd - Current working directory for resolving relative paths
 * @param importer - Function to dynamically import modules (default: native import)
 * @returns Array of loaded Gadget instances
 * @throws Error if module fails to load, contains no gadgets, or initialization fails
 */
export async function loadGadgets(
  specifiers: string[],
  cwd: string,
  importer: GadgetImportFunction = createTypeScriptImporter(),
): Promise<AbstractGadget[]> {
  const gadgets: AbstractGadget[] = [];
  // Track if we're using a custom importer (for testing) - skip external package resolution
  const usingDefaultImporter = importer.toString().includes("import(specifier)");

  for (const specifier of specifiers) {
    // Try builtin resolution first
    const builtin = tryResolveBuiltin(specifier);
    if (builtin) {
      gadgets.push(builtin);
      continue;
    }

    // Try external package resolution (npm/git with presets, versions, etc.)
    // Skip this when using a custom importer (for testing)
    if (usingDefaultImporter && isExternalPackageSpecifier(specifier)) {
      try {
        const externalGadgets = await loadExternalGadgets(specifier);
        gadgets.push(...externalGadgets);
        continue;
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(`Failed to load external package '${specifier}': ${message}`);
      }
    }

    // Fall back to file/npm resolution
    // Check for /GadgetName suffix on local file paths
    const localSpec = isFileLikeSpecifier(specifier) ? parseLocalSpecifier(specifier) : null;
    const pathToResolve = localSpec?.path ?? specifier;
    const gadgetNameFilter = localSpec?.gadgetName;

    const resolved = resolveGadgetSpecifier(pathToResolve, cwd);
    let exports: unknown;
    try {
      exports = await importer(resolved);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to load gadget module '${specifier}': ${message}`);
    }

    let extracted: AbstractGadget[];
    try {
      extracted = extractGadgetsFromModule(exports);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to initialize gadgets from module '${specifier}': ${message}`);
    }

    // Filter by gadget name if specified
    if (gadgetNameFilter) {
      const filtered = extracted.filter(
        (g) => g.name?.toLowerCase() === gadgetNameFilter.toLowerCase(),
      );
      if (filtered.length === 0) {
        throw new Error(
          `Gadget '${gadgetNameFilter}' not found in module '${pathToResolve}'. ` +
            `Available gadgets: ${extracted.map((g) => g.name).join(", ")}`,
        );
      }
      extracted = filtered;
    }

    if (extracted.length === 0) {
      throw new Error(`Module '${specifier}' does not export any Gadget instances.`);
    }
    gadgets.push(...extracted);
  }

  return gadgets;
}
